import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { useSeo } from "../lib/hooks";

export function PaymentSuccess() {
  useSeo({ title: "Confirmation de commande" });
  const [sp] = useSearchParams();
  const { clear } = useCart();
  const [state, setState] = useState({ status: "checking" });
  const tries = useRef(0);

  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) { setState({ status: "error" }); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { clear(); setState({ status: "paid", order: data.order_number, token: data.access_token, isTest: data.is_test }); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState({ status: "error" }); return; }
      } catch { /* retry */ }
      tries.current += 1;
      if (tries.current < 15) timer = setTimeout(poll, 2000);
      else setState({ status: "timeout" });
    };
    poll();
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  return (
    <div className="max-w-xl mx-auto px-5 py-28 text-center" data-testid="payment-success-page">
      {state.status === "checking" && (<><Loader2 className="h-10 w-10 mx-auto text-[#C9A45C] animate-spin" /><p className="mt-6 font-display text-3xl">Vérification du paiement…</p></>)}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="h-12 w-12 mx-auto text-[#C9A45C]" />
          <h1 className="mt-6 font-display text-4xl sm:text-5xl" data-testid="payment-success-title">Merci pour votre commande</h1>
          <p className="mt-4 text-[#F8F6F1]/65">Votre commande <strong className="text-[#C9A45C]" data-testid="order-number">{state.order}</strong> est confirmée.</p>
          {state.isTest && <p className="mt-2 text-xs text-[#F8F6F1]/45">Commande de test — aucun paiement réel n'a été effectué.</p>}
          <Link to={`/suivi/${state.order}?t=${state.token}`} className="btn-gold mt-10" data-testid="payment-success-track-link">Suivre ma commande</Link>
          <Link to="/boutique" className="block mt-5 text-sm text-[#F8F6F1]/55 hover:text-[#C9A45C]">Continuer mes découvertes</Link>
        </>
      )}
      {(state.status === "error" || state.status === "timeout") && (
        <>
          <XCircle className="h-12 w-12 mx-auto text-red-400" />
          <h1 className="mt-6 font-display text-4xl">Paiement non confirmé</h1>
          <p className="mt-4 text-[#F8F6F1]/65">{state.status === "timeout" ? "La confirmation prend plus de temps que prévu. Actualisez la page dans quelques instants." : "Le paiement n'a pas abouti. Votre panier est conservé."}</p>
          <Link to="/commande" className="btn-outline-gold mt-10">Retour à la commande</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  useSeo({ title: "Paiement annulé" });
  return (
    <div className="max-w-xl mx-auto px-5 py-28 text-center" data-testid="payment-cancel-page">
      <XCircle className="h-12 w-12 mx-auto text-[#C9A45C]/70" />
      <h1 className="mt-6 font-display text-4xl">Paiement annulé</h1>
      <p className="mt-4 text-[#F8F6F1]/65">Aucun montant n'a été débité. Votre panier vous attend.</p>
      <Link to="/commande" className="btn-gold mt-10" data-testid="payment-cancel-back">Retour à la commande</Link>
    </div>
  );
}
